import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { updateScoreAfterActivity } from '@/components/utils/LeadScoreUpdater';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ClipboardList, Calendar, AlertTriangle, CheckCircle2, RefreshCw, MapPin } from 'lucide-react';
import TaskCard from '@/components/tasks/TaskCard';
import TaskCompleteModal from '@/components/modals/TaskCompleteModal';
import { completeTask, updateLeadStatusByOutcome, sortTasksByPriority, TASK_OUTCOMES, normalizeTaskType } from '@/components/tasks/TaskStateMachine';
import { isToday, isPast, isFuture, parseISO, startOfDay } from 'date-fns';
import { useAuth } from '@/api/AuthContext';
import { toast } from 'sonner';
import { notifyTaskCompleted } from '@/components/notifications/notificationHelper';

// Bugfix: Lieferung ve Reklamation artık doğru eşleniyor
const TASK_TYPE_TO_ACTIVITY = {
  'Besuchen': 'Besuch',
  'Anrufen': 'Anruf',
  'Angebot': 'Angebot',
  'Nachfassen': 'Nachfassen',
  'Lieferung': 'Lieferung',
  'Reklamation': 'Reklamation',
  'Bedarfsabfrage': 'Bedarfsabfrage',
};

function mapOutcomeToScoreType(outcome) {
  const map = {
    'interested': 'positive_feedback',
    'order': 'order_created',
    'accepted': 'order_created',
    'sent': 'quote_sent',
    'send_offer': 'quote_sent',
    'positive': 'positive_feedback',
    'not_interested': 'negative_feedback',
    'rejected': 'negative_feedback',
    'not_reached': 'no_response',
  };
  return map[outcome] || 'task_completed';
}

function buildActivityNotes(task, outcome, notes, followUpTask) {
  const parts = [`${task.task_type}: ${outcome}`];
  if (notes) parts.push(notes);
  if (followUpTask) parts.push(`→ Wiedervorlage: ${followUpTask.task_type} am ${followUpTask.due_date}`);
  return parts.join(' | ');
}

export default function AgentTasks() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('today');
  const [selectedTask, setSelectedTask] = useState(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading, refetch } = useQuery({
    queryKey: ['agent-tasks', user?.email, user?.name],
    // assigned_agent bazı kayıtlarda E-Mail, bazılarında İsim olarak
    // tutuluyor. Filtre SUNUCUDA kalmalı (diğer agent'ların müşteri verisi
    // tarayıcıya inmemeli) — adapter dizi değerini .in() ile çevirir,
    // böylece her iki kimlik tek sorguda eşleşir.
    queryFn: () => base44.entities.Task.filter(
      { status: 'Offen', assigned_agent: [user?.email, user?.name].filter(Boolean) },
      'due_date'
    ),
    enabled: !!user?.email,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Smart tab auto-selection: show whichever tab has tasks
  useEffect(() => {
    if (tasks.length === 0 || filter !== 'today') return;
    const todayTasks = tasks.filter(t => !t.due_date || isToday(parseISO(t.due_date)));
    if (todayTasks.length === 0) {
      const overdueTasks = tasks.filter(t => t.due_date && isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date)));
      if (overdueTasks.length > 0) {
        setFilter('overdue');
      } else {
        setFilter('upcoming');
      }
    }
  }, [tasks, filter]);

  const filterTasks = (taskList) => {
    const today = startOfDay(new Date());

    return taskList.filter(task => {
      const dueDate = task.due_date ? startOfDay(parseISO(task.due_date)) : null;

      switch (filter) {
        case 'today':
          return !dueDate || isToday(dueDate);
        case 'overdue':
          return dueDate && isPast(dueDate) && !isToday(dueDate);
        case 'upcoming':
          return dueDate && isFuture(dueDate);
        default:
          return true;
      }
    });
  };

  const filteredTasks = sortTasksByPriority(filterTasks(tasks));
  const overdueCount = tasks.filter(t => t.due_date && isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date))).length;
  const todayCount = tasks.filter(t => !t.due_date || isToday(parseISO(t.due_date))).length;

  // Route Planning: collect today's open tasks that have an address
  const todayTasksWithAddress = tasks.filter(
    t => t.due_date && isToday(parseISO(t.due_date)) && t.lead_address
  );

  const handlePlanRoute = () => {
    if (todayTasksWithAddress.length === 0) return;

    const addresses = todayTasksWithAddress.map(t => t.lead_address);
    const destination = encodeURIComponent(addresses[addresses.length - 1]);
    const waypoints = addresses.length > 1
      ? addresses.slice(0, -1).map(a => encodeURIComponent(a)).join('|')
      : '';

    const buildUrl = (origin) => {
      let url = `https://www.google.com/maps/dir/?api=1`;
      if (origin) {
        url += `&origin=${origin}`;
      }
      url += `&destination=${destination}`;
      if (waypoints) {
        url += `&waypoints=${waypoints}`;
      }
      url += `&travelmode=driving`;
      return url;
    };

    // Konum izni açıklayıcı toast — kullanıcı reddederse yine route açılır
    if (navigator.geolocation) {
      const toastId = toast.loading('Standort wird ermittelt...', {
        description: 'Erlauben Sie den Standortzugriff für die optimale Route ab Ihrem aktuellen Ort.',
      });
      navigator.geolocation.getCurrentPosition(
        (position) => {
          toast.dismiss(toastId);
          const origin = `${position.coords.latitude},${position.coords.longitude}`;
          window.open(buildUrl(origin), '_blank');
        },
        () => {
          toast.dismiss(toastId);
          toast.info('Route ohne Startort geöffnet', {
            description: 'Standort nicht verfügbar — Google Maps verwendet den ersten Stopp als Start.',
          });
          window.open(buildUrl(null), '_blank');
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    } else {
      window.open(buildUrl(null), '_blank');
    }
  };

  const handleCompleteTask = async ({ task, outcome, notes, customDate }) => {
    setIsCompleting(true);
    try {
      // ── ALAN 1: Task tamamlama + follow-up zinciri ──
      const newTask = await completeTask(task, outcome, notes, customDate);

      // ── ALAN 2: Lead güncellemesi (durum + skor + son aktivite) ──
      if (task.lead_id) {
        await updateLeadStatusByOutcome(task.lead_id, outcome, task.task_type || task.typ);
        try {
          await updateScoreAfterActivity(task.lead_id, mapOutcomeToScoreType(outcome), { outcome });
        } catch (e) {
          console.warn('Lead score update failed:', e);
        }
      }

      // ── ALAN 3: Zengin Aktivite Raporu ──
      try {
        await base44.entities.ActivityReport.create({
          agent_email: user.email,
          agent_name: user.name || '',
          report_date: new Date().toISOString().split('T')[0],
          activity_type: TASK_TYPE_TO_ACTIVITY[task.task_type] || 'Sonstiges',
          lead_id: task.lead_id,
          lead_name: task.lead_name,
          task_id: task.id,
          notes: buildActivityNotes(task, outcome, notes, newTask),
        });
      } catch (e) {
        console.warn('Activity report creation failed:', e);
      }

      await queryClient.invalidateQueries(['agent-tasks']);
      setCompleteModalOpen(false);
      setSelectedTask(null);

      // Bildirimde sonucun NE olduğu da görünsün (Ergebnis + Notiz)
      const outcomeLabel = TASK_OUTCOMES[normalizeTaskType(task.task_type || task.typ)]
        ?.find((o) => o.value === outcome)?.label || outcome;
      notifyTaskCompleted(task, user.email, user.name, { ergebnis: outcomeLabel, notes });
      toast.success(newTask
        ? 'Aufgabe abgeschlossen — Wiedervorlage erstellt'
        : 'Aufgabe abgeschlossen');
    } catch (error) {
      console.error('Task completion error:', error);
      toast.error('Fehler beim Abschließen: ' + error.message);
    } finally {
      setIsCompleting(false);
    }
  };

  const openCompleteModal = (task) => {
    setSelectedTask(task);
    setCompleteModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand-950 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-slate-900">Aufgaben</h1>
                <p className="text-sm text-slate-500">{tasks.length} offen</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePlanRoute}
                disabled={todayTasksWithAddress.length === 0}
                className="flex items-center gap-1.5"
                title={
                  todayTasksWithAddress.length === 0
                    ? 'Keine heutigen Aufgaben mit Adresse'
                    : `Route mit ${todayTasksWithAddress.length} Stopp${todayTasksWithAddress.length > 1 ? 's' : ''} planen`
                }
              >
                <MapPin className="w-4 h-4" />
                <span className="hidden sm:inline">Route planen</span>
                {todayTasksWithAddress.length > 0 && (
                  <Badge className="ml-0.5 h-5 px-1.5 bg-brand-600">{todayTasksWithAddress.length}</Badge>
                )}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => refetch()}>
                <RefreshCw className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="w-full grid grid-cols-3 bg-slate-100">
              <TabsTrigger value="today" className="relative">
                <Calendar className="w-4 h-4 mr-1.5" />
                Heute
                {todayCount > 0 && (
                  <Badge className="ml-1.5 h-5 px-1.5 bg-brand-600">{todayCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="overdue" className="relative">
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                Überfällig
                {overdueCount > 0 && (
                  <Badge className="ml-1.5 h-5 px-1.5 bg-red-600">{overdueCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="upcoming">
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Kommend
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Task List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 xl:grid-cols-3 md:gap-3">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-4">
                <Skeleton className="h-6 w-1/3 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-3" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </Card>
            ))}
          </div>
        ) : filteredTasks.length > 0 ? (
          <div className="space-y-3">
            {filteredTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onComplete={openCompleteModal}
              />
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500 opacity-50" />
            <p className="text-slate-600 font-medium">Keine Aufgaben</p>
            <p className="text-sm text-slate-400">
              {filter === 'today' && 'Keine Aufgaben für heute'}
              {filter === 'overdue' && 'Keine überfälligen Aufgaben'}
              {filter === 'upcoming' && 'Keine kommenden Aufgaben'}
            </p>
          </Card>
        )}
      </div>

      {/* Complete Modal */}
      <TaskCompleteModal
        task={selectedTask}
        open={completeModalOpen}
        onOpenChange={setCompleteModalOpen}
        onComplete={handleCompleteTask}
        isLoading={isCompleting}
      />
    </div>
  );
}
